import { chunk, subscribeOnce } from '@tmx/core';
import { DriverImport, FleetApi } from '@tmx/data-access';
import { Observable, pipe, switchMap } from 'rxjs';
import { z } from 'zod/v4';

import { DriversImportResponse } from '../../models/drivers-import-response';
import { getErrorContext } from '../../utils/get-error-context';
import { reportToTrackjs } from '../../utils/report-to-trackjs';
import { ImportResponseEmitter } from '../models/import-response-emitter';
import { driversImportResultsSchema } from '../schemas/drivers-import-results.schema';
import { catalogDriversImportResponse } from './catalog-drivers-import-response';

const CHUNK_SIZE = 1000;

/** chimport - CHunked IMPORT. 🐒 */
export function chimportDrivers(trackjsSource: string) {
  const emitter = new ImportResponseEmitter<DriversImportResponse>();
  const importResponse$ = new Observable<DriversImportResponse>(
    (subscriber) => {
      emitter.register(subscriber);
    }
  );

  const chimport = (drivers: DriverImport[]) => {
    const importResponse = new DriversImportResponse(0, 0, 0, [], 0, true);

    let responseCount = 0;
    const subsets = chunk(drivers, CHUNK_SIZE);

    const emit = () => {
      responseCount += 1;
      if (responseCount === subsets.length) {
        emitter.emit(importResponse);
      }
    };

    for (const subset of subsets) {
      const recordUnrecoverableError = () => {
        importResponse.unrecoverable += subset.length;
        emit();
      };

      subscribeOnce(
        FleetApi.postDriversImport(subset),
        (response) => {
          for (const result of driversImportResultsSchema.parse(
            response.data
          )) {
            catalogDriversImportResponse(
              result.validationResult,
              result.importResult,
              result.item.driverId,
              result.item,
              importResponse
            );
          }
          emit();
        },
        (error) => {
          reportToTrackjs(
            trackjsSource,
            'Error importing drivers',
            getErrorContext(error)
          );

          if ((error as Response).status === 400) {
            const wrappedResults = z
              .object({
                error: z.object({
                  data: driversImportResultsSchema,
                }),
              })
              .safeParse(error);
            if (wrappedResults.success) {
              for (const result of wrappedResults.data.error.data) {
                catalogDriversImportResponse(
                  result.validationResult,
                  result.importResult,
                  result.item.driverId,
                  result.item,
                  importResponse
                );
              }
              emit();
            } else {
              recordUnrecoverableError();
            }
          } else {
            recordUnrecoverableError();
          }
        }
      );
    }
  };

  return pipe(
    switchMap(({ drivers }: { drivers: DriverImport[] }) => {
      chimport(drivers);
      return importResponse$;
    })
  );
}
